import React, { useState, useEffect } from 'react';
import { useShipment } from '../contexts/ShipmentContext';

const STORAGE_KEY = 'shipment_data';

const currencyLabels = {
  USD: '$',
  EUR: '€',
  CNY: '¥',
  AED: 'د.إ',
  GBP: '£',
  JPY: '¥',
  CAD: '$',
  AUD: '$',
  CHF: '₣',
  RUB: '₽',
  INR: '₹',
  BRL: 'R$',
  KRW: '₩',
  TRY: '₺',
  ZAR: 'R'
};

// Структура данных по умолчанию
const defaultShipmentData = {
  // Shipment Details
  destinationCountry: '',
  destinationPort: '',
  freightMode: 'Sea',
  packagingType: 'Container 20ft',
  grossWeight: 0,
  netWeight: 0,

  // Cost Breakdown
  costs: {
    // Origin Costs
    originInlandFreight: { value: 0, currency: 'USD' },
    exportCustomsClearance: { value: 0, currency: 'USD' },

    // Main Carriage
    seaAirFreight: { value: 0, currency: 'USD' },
    freightSurcharges: { value: 0, currency: 'USD' },
    cargoInsurance: { value: 0, currency: 'USD' },

    // Destination Costs
    customsDutyPercent: 0,
    vatPercent: 0,
    customsBrokerage: { value: 0, currency: 'USD' },
    portTerminalHandling: { value: 0, currency: 'USD' },
    inlandFreightDestination: { value: 0, currency: 'USD' },
    bankLCCharges: { value: 0, currency: 'USD' },
    otherCharges: { value: 0, currency: 'USD' }
  }
};

// Компонент для полей с выбором валюты
const CurrencyAmountField = ({ label, value, currency, onValueChange, onCurrencyChange, hint, currencySymbols }) => {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="number"
          value={value}
          onChange={(e) => onValueChange(parseFloat(e.target.value) || 0)}
          style={{
            flex: 1,
            height: '38px',
            padding: '8px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
        <select
          value={currency}
          onChange={(e) => onCurrencyChange(e.target.value)}
          style={{
            padding: '8px 12px',
            height: '38px',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: 'white',
            cursor: 'pointer',
            minWidth: '80px'
          }}
        >
          {Object.entries(currencySymbols).map(([code, symbol]) => (
            <option key={code} value={code}>
              {code} ({symbol})
            </option>
          ))}
        </select>
      </div>
      {hint && <span style={{ fontSize: '9px', color: '#9CA3AF' }}>{hint}</span>}
    </div>
  );
};

function ShipmentDataForm({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState(defaultShipmentData);
  const [activeTab, setActiveTab] = useState('shipment');
  const { updateShipmentData } = useShipment();

  useEffect(() => {
    if (isOpen) {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setFormData(parsed);
        } catch (e) {
          console.error('Failed to parse saved data', e);
        }
      }
    }
  }, [isOpen]);

  // Функция для обновления полей shipment
  const updateShipmentField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Функция для обновления простых числовых полей в costs (проценты)
  const updateCostField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      costs: {
        ...prev.costs,
        [field]: value
      }
    }));
  };

  // Функции для обновления полей с валютой
  const updateCostValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      costs: {
        ...prev.costs,
        [field]: {
          ...prev.costs[field],
          value: value
        }
      }
    }));
  };

  const updateCostCurrency = (field, currency) => {
    setFormData(prev => ({
      ...prev,
      costs: {
        ...prev.costs,
        [field]: {
          ...prev.costs[field],
          currency: currency
        }
      }
    }));
  };

  const handleSave = () => {
    updateShipmentData(formData);
    if (onSave) onSave(formData);
    onClose();
  };

  const handleReset = () => {
    if (window.confirm('Reset all shipment data to default values?')) {
      setFormData(defaultShipmentData);
      updateShipmentData(defaultShipmentData);
    }
  };

  const getCurrencyLabel = (code) => currencyLabels[code] || code;
  const savedCurrency = localStorage.getItem('selected_currency') || 'USD';
  const currencySymbol = getCurrencyLabel(savedCurrency);
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: '#fff',
          zIndex: 10
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>🚢 Shipment Data Configuration</h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
              Configure shipping parameters — EXW cost is taken from product price
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6B7280',
              padding: '4px 8px'
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #E5E7EB',
          padding: '0 20px',
          gap: '8px'
        }}>
          <button
            onClick={() => setActiveTab('shipment')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'shipment' ? '#2563EB' : '#6B7280',
              borderBottom: activeTab === 'shipment' ? '2px solid #2563EB' : 'none',
              marginBottom: '-1px'
            }}
          >
            📦 Shipment Details
          </button>
          <button
            onClick={() => setActiveTab('costs')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'costs' ? '#2563EB' : '#6B7280',
              borderBottom: activeTab === 'costs' ? '2px solid #2563EB' : 'none',
              marginBottom: '-1px'
            }}
          >
            💰 Cost Breakdown
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* SHIPMENT DETAILS TAB */}
          {activeTab === 'shipment' && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Destination Country
                  </label>
                  <input
                    type="text"
                    value={formData.destinationCountry}
                    onChange={(e) => updateShipmentField('destinationCountry', e.target.value)}
                    placeholder="e.g., Germany, France, Italy"
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Destination Port / City
                  </label>
                  <input
                    type="text"
                    value={formData.destinationPort}
                    onChange={(e) => updateShipmentField('destinationPort', e.target.value)}
                    placeholder="e.g., Hamburg, Rotterdam, Antwerp"
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Freight Mode
                  </label>
                  <select
                    value={formData.freightMode}
                    onChange={(e) => updateShipmentField('freightMode', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: '#fff'
                    }}
                  >
                    <option value="Sea">🚢 Sea Freight</option>
                    <option value="Air">✈️ Air Freight</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Packaging Type
                  </label>
                  <select
                    value={formData.packagingType}
                    onChange={(e) => updateShipmentField('packagingType', e.target.value)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: '#fff'
                    }}
                  >
                    <option value="Container 20ft">Container 20ft</option>
                    <option value="Container 40ft">Container 40ft</option>
                    <option value="IBC">IBC Totes</option>
                    <option value="Drums">Drums</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Gross Weight (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.grossWeight}
                    onChange={(e) => updateShipmentField('grossWeight', parseFloat(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{ fontSize: '10px', color: '#6B7280' }}>
                    ≈ {(formData.grossWeight / 1000).toFixed(1)} tons
                  </span>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Net Weight (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.netWeight}
                    onChange={(e) => updateShipmentField('netWeight', parseFloat(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '12px',
                background: '#EFF6FF',
                borderRadius: '8px',
                border: '1px solid #BFDBFE'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#1E40AF', marginBottom: '6px' }}>
                  ℹ️ Important
                </div>
                <div style={{ fontSize: '10px', color: '#1E3A8A' }}>
                  EXW cost will be taken automatically from the product price (per ton) and multiplied by the shipment weight.
                  All costs below are entered as <strong>fixed amounts per entire shipment</strong>.
                </div>
              </div>
            </div>
          )}

          {/* COST BREAKDOWN TAB */}
{activeTab === 'costs' && (
  <div>
    {/* Origin Costs */}
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '12px', paddingBottom: '6px', borderBottom: '2px solid #E5E7EB' }}>
        🏭 Origin Costs (UAE) — Fixed per Shipment
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>

        <CurrencyAmountField
          label="Origin Inland Freight"
          value={formData.costs.originInlandFreight?.value || 0}
          currency={formData.costs.originInlandFreight?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('originInlandFreight', value)}
          onCurrencyChange={(currency) => updateCostCurrency('originInlandFreight', currency)}
          hint="Trucking JAFZA → Jebel Ali Port"
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Export Customs Clearance"
          value={formData.costs.exportCustomsClearance?.value || 0}
          currency={formData.costs.exportCustomsClearance?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('exportCustomsClearance', value)}
          onCurrencyChange={(currency) => updateCostCurrency('exportCustomsClearance', currency)}
          currencySymbols={currencyLabels}
        />

      </div> {/* Закрываем grid */}
    </div> {/* Закрываем Origin Costs */}

    {/* Main Carriage */}
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '12px', paddingBottom: '6px', borderBottom: '2px solid #E5E7EB' }}>
        🚢 Main Carriage
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>

        <CurrencyAmountField
          label="Sea/Air Freight"
          value={formData.costs.seaAirFreight?.value || 0}
          currency={formData.costs.seaAirFreight?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('seaAirFreight', value)}
          onCurrencyChange={(currency) => updateCostCurrency('seaAirFreight', currency)}
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Freight Surcharges"
          value={formData.costs.freightSurcharges?.value || 0}
          currency={formData.costs.freightSurcharges?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('freightSurcharges', value)}
          onCurrencyChange={(currency) => updateCostCurrency('freightSurcharges', currency)}
          hint="BAF, CAF, PSS, EBS, etc."
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Cargo Insurance"
          value={formData.costs.cargoInsurance?.value || 0}
          currency={formData.costs.cargoInsurance?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('cargoInsurance', value)}
          onCurrencyChange={(currency) => updateCostCurrency('cargoInsurance', currency)}
          currencySymbols={currencyLabels}
        />

      </div> {/* Закрываем grid */}
    </div> {/* Закрываем Main Carriage */}

    {/* Destination Costs */}
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '12px', paddingBottom: '6px', borderBottom: '2px solid #E5E7EB' }}>
        🎯 Destination Costs
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>

        {/* Проценты без валюты */}
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
            Customs Duty (%)
          </label>
          <input
            type="number"
            value={formData.costs.customsDutyPercent}
            onChange={(e) => updateCostField('customsDutyPercent', parseFloat(e.target.value) || 0)}
            style={{
              width: '100%',
              height: '38px',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
            VAT (%)
          </label>
          <input
            type="number"
            value={formData.costs.vatPercent}
            onChange={(e) => updateCostField('vatPercent', parseFloat(e.target.value) || 0)}
            style={{
              width: '100%',
              height: '38px',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Поля с валютой */}
        <CurrencyAmountField
          label="Customs Brokerage"
          value={formData.costs.customsBrokerage?.value || 0}
          currency={formData.costs.customsBrokerage?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('customsBrokerage', value)}
          onCurrencyChange={(currency) => updateCostCurrency('customsBrokerage', currency)}
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Port Terminal Handling"
          value={formData.costs.portTerminalHandling?.value || 0}
          currency={formData.costs.portTerminalHandling?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('portTerminalHandling', value)}
          onCurrencyChange={(currency) => updateCostCurrency('portTerminalHandling', currency)}
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Inland Freight (Destination)"
          value={formData.costs.inlandFreightDestination?.value || 0}
          currency={formData.costs.inlandFreightDestination?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('inlandFreightDestination', value)}
          onCurrencyChange={(currency) => updateCostCurrency('inlandFreightDestination', currency)}
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Bank LC Charges"
          value={formData.costs.bankLCCharges?.value || 0}
          currency={formData.costs.bankLCCharges?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('bankLCCharges', value)}
          onCurrencyChange={(currency) => updateCostCurrency('bankLCCharges', currency)}
          currencySymbols={currencyLabels}
        />

        <CurrencyAmountField
          label="Other Charges"
          value={formData.costs.otherCharges?.value || 0}
          currency={formData.costs.otherCharges?.currency || 'USD'}
          onValueChange={(value) => updateCostValue('otherCharges', value)}
          onCurrencyChange={(currency) => updateCostCurrency('otherCharges', currency)}
          currencySymbols={currencyLabels}
        />

      </div>
    </div>
  </div>
)}

          {/* Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #E5E7EB'
          }}>
            <button
              onClick={handleReset}
              style={{
                padding: '8px 16px',
                background: '#F3F4F6',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#374151'
              }}
            >
              Reset to Defaults
            </button>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 20px',
                  background: '#F3F4F6',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: '#374151'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '8px 24px',
                  background: '#2563EB',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: '#fff'
                }}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShipmentDataForm;

